import React, { useState } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface SafetyScreeningTemplateProps {
  stage: WorkflowStage
  onSelect: (value: AnswerValue) => void
  currentPhase?: PhaseInfo | null
  savedAnswers?: Record<string, unknown>
}

interface SafetyQuestion {
  id: string
  question: string
  isCritical: boolean
  showDividerAfter?: boolean
}

const SAFETY_QUESTIONS: SafetyQuestion[] = [
  { id: 'pregnant_planning', question: 'Are you currently pregnant or planning to become pregnant?', isCritical: true },
  { id: 'breastfeeding', question: 'Are you currently breastfeeding?', isCritical: true },
  { id: 'mtc_men2', question: 'Do you have a personal or family history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN 2)?', isCritical: true },
  { id: 'pancreatitis', question: 'Have you ever had pancreatitis?', isCritical: false, showDividerAfter: true },
  { id: 'gastroparesis', question: 'Have you been diagnosed with gastroparesis (delayed stomach emptying)?', isCritical: false },
  { id: 'bowel_obstruction', question: 'Do you have a history of bowel obstruction?', isCritical: false },
  { id: 'eating_disorder', question: 'Have you been diagnosed with an eating disorder?', isCritical: false },
]

export default function SafetyScreeningTemplate({
  onSelect,
  savedAnswers,
}: SafetyScreeningTemplateProps) {
  const [answers, setAnswers] = useState<Record<string, boolean | null>>(() => {
    const saved = savedAnswers?.safety_answers as Record<string, boolean> | undefined
    if (saved && typeof saved === 'object') {
      return Object.fromEntries(
        SAFETY_QUESTIONS.map((q) => [q.id, saved[q.id] ?? false])
      )
    }
    return Object.fromEntries(SAFETY_QUESTIONS.map((question) => [question.id, false]))
  })

  const handleAnswer = (questionId: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const allAnswered = SAFETY_QUESTIONS.every((q) => answers[q.id] !== undefined && answers[q.id] !== null)
  const hasCriticalContraindication = SAFETY_QUESTIONS.filter((q) => q.isCritical).some((q) => answers[q.id] === true)
  const hasGiConcerns = ['gastroparesis', 'bowel_obstruction'].some((id) => answers[id] === true)
  const hasEatingDisorderFlag = answers['eating_disorder'] === true

  const handleContinue = () => {
    const contraindications = SAFETY_QUESTIONS.filter((q) => answers[q.id] === true).map((q) => q.id)
    onSelect({
      contraindications,
      has_critical_contraindication: hasCriticalContraindication,
      has_gi_concerns: hasGiConcerns,
      has_eating_disorder_flag: hasEatingDisorderFlag,
      safety_answers: answers,
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="mb-2">
        <div className="space-y-2.5">
          {SAFETY_QUESTIONS.map((question) => {
            const answer = answers[question.id]
            return (
              <React.Fragment key={question.id}>
                <div
                  className={`flex items-center justify-between gap-3 py-3 px-3.5 rounded-xl border glass transition-all ${
                    answer === true && question.isCritical
                      ? 'border-red-500/30 bg-red-500/10'
                      : answer === true
                        ? 'border-amber-500/30 bg-amber-500/10'
                        : 'bg-[var(--ww-surface)] border-[var(--ww-glass-border)]'
                  }`}
                >
                  <p className="text-[13px] font-medium text-[var(--ww-text)] flex-1 leading-snug">{question.question}</p>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => handleAnswer(question.id, false)}
                      className={`py-1 px-3.5 rounded-full text-[12px] font-semibold transition-all border-2 whitespace-nowrap ${
                        answer === false
                          ? 'bg-[rgba(74,138,154,0.15)] border-[var(--ww-accent)] text-[var(--ww-accent)] shadow-[0_0_8px_rgba(74,138,154,0.25)]'
                          : 'border-[var(--ww-glass-border)] bg-transparent text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)]'
                      }`}
                    >
                      No
                    </button>
                    <button
                      onClick={() => handleAnswer(question.id, true)}
                      className={`py-1 px-3.5 rounded-full text-[12px] font-semibold transition-all border-2 whitespace-nowrap ${
                        answer === true
                          ? 'bg-[rgba(204,85,0,0.2)] border-[#CC5500] text-[#CC5500] shadow-[0_0_8px_rgba(204,85,0,0.25)]'
                          : 'border-[var(--ww-glass-border)] bg-transparent text-[var(--ww-text-2)] hover:border-[var(--ww-border-hover)]'
                      }`}
                    >
                      Yes
                    </button>
                  </div>
                </div>
                {question.showDividerAfter && <div className="my-2 border-t border-[var(--ww-glass-border)]" />}
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {hasCriticalContraindication && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-400" />
          <div>
            <p className="mb-1 text-sm font-medium text-red-300">Important Safety Information</p>
            <p className="text-sm text-red-300/80">
              Based on your answers, GLP-1 medications may not be appropriate for you. Our clinical team will review your information and discuss options with you.
            </p>
          </div>
        </div>
      )}

      {(hasGiConcerns || hasEatingDisorderFlag) && !hasCriticalContraindication && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-400" />
          <p className="text-sm text-amber-300/80">
            Our clinical team will carefully review this information to ensure any treatment is appropriate and safe for you.
          </p>
        </div>
      )}

      <div className="pt-4 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          onClick={handleContinue}
          disabled={!allAnswered}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
